import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, Input, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import {
  PrCard,
  PrCardType,
} from '@app/components/pr-card/pr-card/pr-card.component';
import { RadioOption } from '@app/components/pr-form/pr-input-radio/pr-input-radio.component';
import { LoanDao } from '@app/dao/loan/loan.dao';
import { extractLoanApiErrorMessages } from '@app/services/loan/loan-api-error.utils';
import {
  buildLoanComputationModel,
  isLoanComputationReady,
  loanPreviewBlockingReasons,
} from '@app/services/loan/loan-computation.utils';
import {
  createLoanPalierGroup,
  fillLoanPaliersToMinimum,
  getLoanPaliersArray,
  refreshLoanPaliersValidity,
} from '@app/services/loan/loan-palier.form';
import {
  LOAN_DIFFERE_NONE,
  LOAN_PALIERS_MAX,
  LOAN_PALIERS_MIN,
  LOAN_PALIER_DURATION_CONTROL,
  LoanComputationResult,
  LoanPalier,
  LoanPalierIssue,
  LoanStep,
} from '@app/services/loan/loan-palier.model';
import {
  isLoanDiffere,
  palierFirstMonthlyPayment,
  palierSegmentStartMonth,
  sumPalierDurations,
  toFiniteNumber,
  validateLoanPaliers,
} from '@app/services/loan/loan-palier.utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

const PREVIEW_DEBOUNCE_MS = 500;

const PREVIEW_INCOMPLETE_REASON =
  'Complétez les informations du crédit et des paliers';

const PREVIEW_EMPTY_MESSAGE =
  'Aucune échéance n’a été calculée pour ces paliers.';

const PREVIEW_UNAVAILABLE_TITLE =
  'L’aperçu de l’échéancier n’est pas encore disponible';

const PREVIEW_FAILED_TITLE = 'Le calcul de l’échéancier n’a pas abouti';

interface LoanPaliersPreview {
  paliers: LoanPalier[] | null;
  result: LoanComputationResult | null;
  errorMessages: string[];
}

@UntilDestroy()
@Component({
  selector: 'ea-loan-paliers',
  templateUrl: './ea-loan-paliers.component.html',
  styleUrls: ['./ea-loan-paliers.component.scss'],
  standalone: false,
})
export class EaLoanPaliersComponent implements OnInit {
  @Input() form!: UntypedFormGroup;
  @Input() derrors = false;
  @Input() startDate: unknown;

  readonly minPaliers = LOAN_PALIERS_MIN;
  readonly maxPaliers = LOAN_PALIERS_MAX;

  readonly enabledOptions: RadioOption[] = [
    { value: false, label: 'Non' },
    { value: true, label: 'Oui' },
  ];

  readonly differeOptions: RadioOption[] = [
    { value: LOAN_DIFFERE_NONE, label: 'Aucun' },
    { value: 'TOTAL', label: 'Total' },
    { value: 'PARTIEL', label: 'Partiel' },
  ];

  readonly previewEmptyMessage = PREVIEW_EMPTY_MESSAGE;
  readonly previewUnavailableTitle = PREVIEW_UNAVAILABLE_TITLE;
  readonly previewFailedTitle = PREVIEW_FAILED_TITLE;

  issues: LoanPalierIssue[] = [];
  durationsSum = 0;
  previewBlockingReasons: string[] = [];

  previewReady = false;
  previewLoading = false;
  previewCards: PrCard[] = [];
  previewSteps: LoanStep[] | null = null;
  previewPaliers: LoanPalier[] | null = null;
  previewErrorMessages: string[] = [];
  lastPalierMonthlyPayment: number | null = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private loanDao: LoanDao,
    private liveAnnouncer: LiveAnnouncer,
  ) {}

  ngOnInit(): void {
    this.watchEnabled();
    this.watchState();
    this.watchPreview();
  }

  get paliers(): UntypedFormArray | null {
    return getLoanPaliersArray(this.form);
  }

  get enabled(): boolean {
    return this.form?.get('paliersEnabled')?.value === true;
  }

  get totalDuration(): number {
    return toFiniteNumber(this.totalDurationValue) ?? 0;
  }

  private get totalDurationValue(): unknown {
    return this.form?.get('durationTotalInMonth')?.value;
  }

  get sumMatches(): boolean {
    return (
      this.totalDuration > 0 &&
      this.durationsSum === this.totalDuration &&
      !this.issues.some(issue => issue.field === LOAN_PALIER_DURATION_CONTROL)
    );
  }

  get sumStatusIcon(): string {
    if (this.sumMatches) {
      return 'check_circle';
    }
    return this.derrors ? 'error_outline' : 'warning';
  }

  get showPreviewSchedule(): boolean {
    return this.previewReady && !this.previewErrorMessages.length;
  }

  get canAddPalier(): boolean {
    return (this.paliers?.length ?? 0) < this.maxPaliers;
  }

  get canRemovePalier(): boolean {
    return (this.paliers?.length ?? 0) > this.minPaliers;
  }

  isLastPalier(index: number): boolean {
    return index === (this.paliers?.length ?? 0) - 1;
  }

  isDiffereRow(index: number): boolean {
    return (
      index === 0 && isLoanDiffere(this.paliers?.at(0)?.get('differe')?.value)
    );
  }

  palierLabel(index: number): string {
    const suffixes = [
      this.isDiffereRow(index) ? 'différé' : null,
      this.isLastPalier(index) ? 'dernier (calculé)' : null,
    ].filter(Boolean);
    return `Palier ${index + 1}${suffixes.length ? ' · ' + suffixes.join(' · ') : ''}`;
  }

  showComputedMonthlyPayment(index: number): boolean {
    return (
      this.isLastPalier(index) &&
      this.lastPalierMonthlyPayment !== null &&
      this.previewPaliers?.length === this.paliers?.length
    );
  }

  addPalier(): void {
    const array = this.paliers;
    if (!array || array.length >= this.maxPaliers) {
      return;
    }
    array.insert(
      Math.max(array.length - 1, 0),
      createLoanPalierGroup(this.formBuilder),
    );
    refreshLoanPaliersValidity(this.form);
  }

  removePalier(index: number): void {
    const array = this.paliers;
    if (!array || array.length <= this.minPaliers) {
      return;
    }
    array.removeAt(index);
    refreshLoanPaliersValidity(this.form);
  }

  private watchEnabled(): void {
    const enabledControl = this.form?.get('paliersEnabled');
    if (!enabledControl) {
      return;
    }
    enabledControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(enabled =>
        enabled === true
          ? fillLoanPaliersToMinimum(this.formBuilder, this.form)
          : refreshLoanPaliersValidity(this.form),
      );
  }

  private watchState(): void {
    if (!this.form) {
      return;
    }
    this.form.valueChanges
      .pipe(startWith(null), untilDestroyed(this))
      .subscribe(() => this.refreshState());
  }

  private watchPreview(): void {
    if (!this.form) {
      return;
    }
    this.form.valueChanges
      .pipe(
        startWith(null),
        map(() => this.computePreviewKey()),
        distinctUntilChanged(),
        tap(key => this.startPreviewRequest(key)),
        debounceTime(PREVIEW_DEBOUNCE_MS),
        switchMap(key => (key ? this.requestPreview(key) : of(null))),
        untilDestroyed(this),
      )
      .subscribe(preview => this.applyPreview(preview));
  }

  private startPreviewRequest(key: string): void {
    this.previewLoading = key !== '';
    this.previewErrorMessages = [];
  }

  private refreshState(): void {
    refreshLoanPaliersValidity(this.form);
    const values = this.paliers?.value ?? [];
    this.durationsSum = sumPalierDurations(values);
    this.issues = this.enabled
      ? validateLoanPaliers(values, this.totalDurationValue)
      : [];
    this.previewReady = this.computePreviewKey() !== '';

    const wasBlocked = this.previewBlockingReasons.length > 0;
    this.previewBlockingReasons =
      this.enabled && !this.previewReady
        ? this.buildPreviewBlockingReasons()
        : [];

    if (!wasBlocked && this.previewBlockingReasons.length) {
      this.liveAnnouncer.announce(PREVIEW_UNAVAILABLE_TITLE);
    }

    if (!this.previewReady) {
      this.previewLoading = false;
      this.resetPreview();
    }
  }

  private buildPreviewBlockingReasons(): string[] {
    const reasons = loanPreviewBlockingReasons(
      buildLoanComputationModel(this.form?.value ?? {}, this.startDate),
      this.issues,
    );

    return reasons.length ? reasons : [PREVIEW_INCOMPLETE_REASON];
  }

  private computePreviewKey(): string {
    if (!this.enabled) {
      return '';
    }
    const rows = this.paliers?.value ?? [];
    if (validateLoanPaliers(rows, this.totalDurationValue).length > 0) {
      return '';
    }
    const model = buildLoanComputationModel(this.form.value, this.startDate);

    return isLoanComputationReady(model) ? JSON.stringify(model) : '';
  }

  private requestPreview(key: string) {
    const model = JSON.parse(key) as LoanComputationResult;
    return this.loanDao.computeLoanModel(model).pipe(
      map(
        (result: LoanComputationResult): LoanPaliersPreview => ({
          paliers: model.paliers ?? null,
          result,
          errorMessages: [],
        }),
      ),
      catchError(error =>
        of<LoanPaliersPreview>({
          paliers: model.paliers ?? null,
          result: null,
          errorMessages: this.buildPreviewErrorMessages(error),
        }),
      ),
    );
  }

  private buildPreviewErrorMessages(error: unknown): string[] {
    const messages = extractLoanApiErrorMessages(error);
    return messages.length
      ? messages
      : ['Le calcul de l’échéancier a échoué. Vérifiez les paliers saisis.'];
  }

  private applyPreview(preview: LoanPaliersPreview | null): void {
    this.previewLoading = false;
    if (!preview) {
      this.resetPreview();
      return;
    }

    this.previewErrorMessages = preview.errorMessages;
    if (preview.errorMessages.length) {
      this.liveAnnouncer.announce(PREVIEW_FAILED_TITLE);
    }
    this.previewPaliers = preview.paliers ?? null;
    this.previewSteps = preview.result?.steps ?? null;
    this.previewCards = preview.result
      ? this.buildPreviewCards(preview.result)
      : [];
    this.lastPalierMonthlyPayment =
      this.resolveLastPalierMonthlyPayment(preview);
  }

  private resolveLastPalierMonthlyPayment(
    preview: LoanPaliersPreview,
  ): number | null {
    const paliers = preview.paliers;
    const steps = preview.result?.steps;
    if (!Array.isArray(paliers) || !paliers.length || !steps) {
      return null;
    }
    return palierFirstMonthlyPayment(
      steps,
      palierSegmentStartMonth(paliers, paliers.length - 1),
    );
  }

  private buildPreviewCards(result: LoanComputationResult): PrCard[] {
    return [
      new PrCard(
        'fa-tag',
        'Mensualité (assurance comprise)',
        PrCardType.EURO,
        result.amountMonthly ?? 0,
        'Première échéance du crédit, assurance comprise',
      ),
      new PrCard(
        'fa-clock-o',
        'Coût total du crédit',
        PrCardType.EURO,
        result.loanTotalCost ?? 0,
        'Intérêts, assurance et frais de dossier',
      ),
      new PrCard(
        'fa-star-o',
        'TAEG',
        PrCardType.PERCENT_WITH_2_DECIMAL,
        result.taeg ?? 0,
        'Taux annuel effectif global',
      ),
    ];
  }

  private resetPreview(): void {
    this.previewCards = [];
    this.previewSteps = null;
    this.previewPaliers = null;
    this.previewErrorMessages = [];
    this.lastPalierMonthlyPayment = null;
  }
}
